import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { BookOpen, Github, Mail, MapPin } from "lucide-react";
import { motion } from "framer-motion";

export function Footer() {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || "fr";
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: t("nav.home"), to: `/${currentLang}/` },
    { label: t("nav.catalog"), to: `/${currentLang}/catalog` },
    { label: t("nav.authors"), to: `/${currentLang}/authors` },
    { label: t("nav.about"), to: `/${currentLang}/about` },
    { label: t("nav.faq"), to: `/${currentLang}/faq` },
  ];

  const legalLinks = [
    {
      label: t("footer.privacy"),
      to: `/${currentLang}/privacy`,
    },
    {
      label: t("footer.terms"),
      to: `/${currentLang}/terms`,
    },
  ];

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="border-t bg-muted/40"
    >
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* ── About ──────────────────────────────────────────────────── */}
          <div className="space-y-4">
            <Link
              to={`/${currentLang}/`}
              className="flex items-center gap-2 text-lg font-heading font-bold"
            >
              <BookOpen className="h-5 w-5 text-primary" />
              <span>Katalog</span>
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("footer.about_text")}
            </p>
          </div>

          {/* ── Quick Links ────────────────────────────────────────────── */}
          <div className="space-y-4">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">
              {t("footer.quick_links")}
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Contact ────────────────────────────────────────────────── */}
          <div className="space-y-4">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">
              {t("footer.contact")}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <Mail className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <a
                  href="mailto:contact@katalog.local"
                  className="transition-colors hover:text-foreground"
                >
                  {t("footer.email_address")}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{t("footer.address_line1")}</span>
              </li>
            </ul>
          </div>

          {/* ── Social / Misc ──────────────────────────────────────────── */}
          <div className="space-y-4">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider">
              {t("footer.social")}
            </h3>
            <div className="flex items-center gap-3">
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                aria-label={t("footer.github_label")}
              >
                <Github className="h-5 w-5" />
              </a>
            </div>

            {/* Legal links */}
            <ul className="space-y-2 pt-2">
              {legalLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Bottom bar ────────────────────────────────────────────────── */}
        <div className="mt-10 border-t pt-6">
          <p className="text-center text-xs text-muted-foreground">
            {t("footer.copyright", { year: currentYear })}
          </p>
        </div>
      </div>
    </motion.footer>
  );
}

export default Footer;
